import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { FLY_LAND_EVENT, isFlightActive } from "../utils/flyToBag";
import SignInModal from "./SignInModal";
import { isAuthenticated, getUserProfile } from "../utils/auth";
import WishlistPopover from "./WishlistPopover";
import CartPopover from "./CartPopover";
import UserMenu, { USER_MENU_ID } from "./UserMenu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import orbieeAvatar from "../images/Orbiee_Avatra.png";
import OrbieeLogo from "../images/brand/orbiee-logo.png";

// Load all avatar icons dynamically using require.context
const avatarIconsContext = require.context("../Avatar_Icons", false, /_Icon\.png$/);
const avatarIcons = {};
avatarIconsContext.keys().forEach((item) => {
  const iconName = item.replace("./", "").replace("_Icon.png", "");
  avatarIcons[iconName] = avatarIconsContext(item);
});

/**
 * Kick the icon, pop the badge and ring the button for `kind` ("cart" or
 * "wishlist"): when a fly-to-bag ghost lands on it, and when its count rises
 * with no ghost on the way (a quantity bump, a server refresh).
 *
 * Purely decorative: it returns refs to pin on the icon, badge and ring and
 * never touches the count itself.
 */
function useBagPulse(kind, count) {
  const iconRef = useRef(null);
  const badgeRef = useRef(null);
  const shockRef = useRef(null);
  const prev = useRef(count);

  const pulse = useCallback(() => {
    [
      [iconRef.current, "ob-kick"],
      [badgeRef.current, "ob-pop"],
      [shockRef.current, "ob-go"],
    ].forEach(([node, cls]) => {
      if (!node) return;
      node.classList.remove(cls);
      // Force a reflow so the animation restarts rather than being ignored
      // as an already-applied class.
      void node.offsetWidth;
      node.classList.add(cls);
    });
  }, []);

  useEffect(() => {
    // A ghost is flying here (or just landed) and kicks on arrival — kicking
    // on the count change as well would play the landing twice.
    if (count > prev.current && !isFlightActive(kind)) pulse();
    prev.current = count;
  }, [count, kind, pulse]);

  useEffect(() => {
    const onLand = (e) => {
      if (e?.detail?.kind === kind) pulse();
    };
    window.addEventListener(FLY_LAND_EVENT, onLand);
    return () => window.removeEventListener(FLY_LAND_EVENT, onLand);
  }, [kind, pulse]);

  return { iconRef, badgeRef, shockRef };
}

export default function TopBar({
  activeMain,
  onWomenClick,
  onMenClick,
  onAccessoriesClick,
  onOnSaleClick,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  onSignInClick,
  isAuthModalOpen,
  onCloseAuthModal,
  totalItems,
  wishlistItems = 0,
  onOpenMenu,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const [wishlistAnchorEl, setWishlistAnchorEl] = useState(null);
  const [cartAnchorEl, setCartAnchorEl] = useState(null);
  const [profileHover, setProfileHover] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [userProfile, setUserProfile] = useState(getUserProfile());
  const [profileImageError, setProfileImageError] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  const cartPulse = useBagPulse("cart", totalItems || 0);
  const wishPulse = useBagPulse("wishlist", wishlistItems || 0);

  const handleWishlistClick = (e) => setWishlistAnchorEl(e.currentTarget);
  const handleWishlistClose = () => setWishlistAnchorEl(null);

  const handleCartClick = (e) => setCartAnchorEl(e.currentTarget);
  const handleCartClose = () => setCartAnchorEl(null);

  // The profile button, signed in, opens the account menu; signed out it
  // opens sign-in. Where "Account" leads (shopper vs admin) and how to sign
  // out are the menu's business, so neither the bar nor Navbar has to know.
  const userMenuOpen = Boolean(userMenuAnchorEl);
  const handleUserMenuClose = () => setUserMenuAnchorEl(null);
  const handleProfileClick = (e) => {
    if (isAuthenticated()) setUserMenuAnchorEl(e.currentTarget);
    else onSignInClick?.();
  };

  // Generate profile picture URL from user's full name
  const getProfilePictureUrl = () => {
    if (!userProfile?.customer_name) return null;

    // Convert customer_name to URL format (e.g., "Emily Parker" -> "emily.parker")
    const userfullname = userProfile.customer_name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ".");

    // Return the icon from the pre-loaded avatarIcons object
    return avatarIcons[userfullname] || null;
  };

  // Update user profile when auth state changes
  useEffect(() => {
    const updateProfile = () => {
      setUserProfile(getUserProfile());
      // Reset image error when profile changes
      setProfileImageError(false);
    };

    // Check immediately
    updateProfile();

    // Listen for auth state change events
    const handleAuthChange = () => {
      setTimeout(updateProfile, 200);
    };

    window.addEventListener("authStateChange", handleAuthChange);
    window.addEventListener("authStateChanged", handleAuthChange);

    return () => {
      window.removeEventListener("authStateChange", handleAuthChange);
      window.removeEventListener("authStateChanged", handleAuthChange);
    };
  }, []);

  // ⌘K / Ctrl+K focuses search — the shortcut every modern storefront has.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const userName = userProfile?.customer_name || "";
  const signedIn = isAuthenticated();
  // One resolution of the picture, shared by the button and the menu header.
  const avatarSrc = profileImageError
    ? orbieeAvatar
    : getProfilePictureUrl() || orbieeAvatar;

  const handleBackClick = () => {
    navigate(-1);
  };
  const navLinks = [
    { label: "New Arrivals", path: "/new-arrivals" },
    {
      label: "Women",
      path: "#",
      onClick: onWomenClick,
      active: activeMain === "WOMEN",
    },
    {
      label: "Men",
      path: "#",
      onClick: onMenClick,
      active: activeMain === "MEN",
    },
    {
      label: "Accessories",
      path: "#",
      onClick: onAccessoriesClick,
      active: activeMain === "ACCESSORIES",
    },

    {
      label: "Sale",
      path: "#",
      onClick: onOnSaleClick,
      active: activeMain === "ONSALE",
      isSale: true,
    },
  ];

  const linkClass = (link) =>
    [
      "ob-topnav-link",
      link.active ? "ob-on" : "",
      link.isSale ? "ob-sale" : "",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <div className="ob-topbar ob-glass">
      <div className="ob-topbar-inner">
        {/* ── Left: back + wordmark ── */}
        <div className="flex items-center gap-2 shrink-0">
          {!isHomePage && (
            <button
              onClick={handleBackClick}
              className="ob-iconbtn ob-ink2"
              aria-label="Go back"
              title="Back"
            >
              <svg
                className="w-[18px] h-[18px]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.9}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
          )}
          {/* The logo carries the name and "Retail assistant" itself, so no
              wordmark text beside it. */}
          <Link to="/" className="ob-brand" aria-label="Orbiee home">
            <img src={OrbieeLogo} alt="Orbiee — Retail assistant" className="ob-brand-logo" />
          </Link>
          <span className="ob-brand-rule" aria-hidden="true" />
        </div>

        {/* ── Centre-left: primary navigation ── */}
        <nav className="ob-topnav" aria-label="Primary">
          {navLinks.map((link, index) => {
            if (link.disabled) {
              return (
                <span key={index} className="ob-topnav-link ob-disabled">
                  {link.label}
                </span>
              );
            }
            if (link.onClick) {
              return (
                <button
                  key={index}
                  type="button"
                  onClick={link.onClick}
                  className={linkClass(link)}
                >
                  {link.label}
                </button>
              );
            }
            return (
              <Link key={index} to={link.path} className={linkClass(link)}>
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* ── Centre: search ── */}
        <div className="ob-topsearch">
          <form
            onSubmit={onSearchSubmit}
            className={`ob-topsearch-form ${searchFocused ? "ob-focus" : ""}`}
            role="search"
          >
            <svg
              className="ob-topsearch-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.85}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search, or just ask Orbiee…"
              className="ob-topsearch-input"
              autoComplete="off"
            />
            
          </form>
        </div>

        {/* ── Right: actions ── */}
        <div className="ob-topactions">
          {isAuthModalOpen && <SignInModal onClose={onCloseAuthModal} />}

          {/* Wishlist */}
          <button
            type="button"
            className="ob-iconbtn"
            onClick={handleWishlistClick}
            aria-label="Wishlist"
            title="Wishlist"
            data-fly-target="wishlist"
          >
            <svg
              ref={wishPulse.iconRef}
              className="w-[19px] h-[19px]"
              viewBox="0 0 24 24"
              fill={wishlistItems > 0 ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: wishlistItems > 0 ? "var(--ob-err)" : undefined }}
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            </svg>
            {wishlistItems > 0 && (
              <span ref={wishPulse.badgeRef} className="ob-badge ob-badge-err">
                {wishlistItems}
              </span>
            )}
            <span ref={wishPulse.shockRef} className="ob-shock ob-shock-wish" aria-hidden="true" />
          </button>

          {/* Cart */}
          <button
            type="button"
            className="ob-iconbtn"
            onClick={handleCartClick}
            aria-label="Shopping cart"
            title="Cart"
            data-fly-target="cart"
          >
            <svg
              ref={cartPulse.iconRef}
              className="w-[19px] h-[19px]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 8h16l-1.3 12.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8Z" />
              <path d="M9 8V6a3 3 0 0 1 6 0v2" />
            </svg>
            {totalItems > 0 && (
              <span ref={cartPulse.badgeRef} className="ob-badge">
                {totalItems}
              </span>
            )}
            <span ref={cartPulse.shockRef} className="ob-shock" aria-hidden="true" />
          </button>

          <span className="ob-divider" aria-hidden="true" />

          {/* Profile */}
          <div
            className="relative"
            onMouseEnter={() => setProfileHover(true)}
            onMouseLeave={() => setProfileHover(false)}
          >
            <button
              type="button"
              onClick={handleProfileClick}
              className="ob-iconbtn ob-avatarbtn"
              aria-label={signedIn ? "Account menu" : "Sign in"}
              aria-haspopup={signedIn ? "menu" : "dialog"}
              aria-expanded={signedIn ? userMenuOpen : undefined}
              aria-controls={userMenuOpen ? USER_MENU_ID : undefined}
            >
              {signedIn && userProfile ? (
                <img
                  src={avatarSrc}
                  alt="Profile"
                  onError={() => setProfileImageError(true)}
                  className="ob-avatarimg"
                />
              ) : (
                <AccountCircleIcon sx={{ fontSize: 22 }} />
              )}
            </button>

            {/* Username tooltip on hover. The open menu names the shopper
                itself, so the two never show together. */}
            {signedIn && userProfile && profileHover && !userMenuOpen && userName && (
              <div className="ob-tooltip" role="tooltip">
                {userName}
              </div>
            )}
          </div>

          {/* Mobile menu */}
          <button
            className="ob-iconbtn ob-menubtn"
            onClick={onOpenMenu}
            aria-label="Open menu"
          >
            <svg
              className="w-[20px] h-[20px]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.9}
              strokeLinecap="round"
            >
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Popovers */}
      <WishlistPopover
        anchorEl={wishlistAnchorEl}
        onClose={handleWishlistClose}
      />
      <CartPopover anchorEl={cartAnchorEl} onClose={handleCartClose} />
      <UserMenu
        anchorEl={userMenuAnchorEl}
        onClose={handleUserMenuClose}
        profile={userProfile}
        avatarSrc={avatarSrc}
      />
    </div>
  );
}
